import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Paper, Tooltip, Typography } from '@mui/material';
import FemaleIcon from '@mui/icons-material/Female';
import MaleIcon from '@mui/icons-material/Male';
import TransgenderIcon from '@mui/icons-material/Transgender';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import { DSP } from '../detailStockSearch.classes';

const GenderChip: FC<{ label: string }> = ({ label }) => {
    const lower = (label || '').toLowerCase();
    let icon: React.ReactElement = <TransgenderIcon sx={{ fontSize: '15px !important' }} />;
    let chipSx: object = { bgcolor: '#f3e5f5', color: '#6a1b9a', borderColor: '#ce93d8' };

    if (
        lower.includes('kadın') ||
        lower.includes('kadin') ||
        lower.includes('bayan') ||
        lower.includes('women') ||
        lower.includes('female')
    ) {
        icon = <FemaleIcon sx={{ fontSize: '15px !important' }} />;
        chipSx = { bgcolor: '#fce4ec', color: '#c2185b', borderColor: '#f48fb1' };
    } else if (lower.includes('erkek') || lower.includes('men') || lower.includes('male')) {
        icon = <MaleIcon sx={{ fontSize: '15px !important' }} />;
        chipSx = { bgcolor: '#e3f2fd', color: '#1565c0', borderColor: '#90caf9' };
    } else if (
        lower.includes('çocuk') ||
        lower.includes('cocuk') ||
        lower.includes('kids') ||
        lower.includes('kız') ||
        lower.includes('kiz')
    ) {
        icon = <TransgenderIcon sx={{ fontSize: '15px !important' }} />;
        chipSx = { bgcolor: '#fff3e0', color: '#e65100', borderColor: '#ffcc80' };
    }

    return (
        <Chip
            size="small"
            icon={icon}
            label={label}
            variant="outlined"
            sx={{
                ...chipSx,
                fontWeight: 600,
                fontSize: '0.72rem',
                height: 24,
                border: '1.5px solid',
                '& .MuiChip-icon': { color: 'inherit' },
            }}
        />
    );
};

const InfoItem: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <Box>
        <Typography
            variant="caption"
            sx={{
                color: 'text.disabled',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.07em',
                fontSize: '0.6rem',
                display: 'block',
                mb: 0.6,
            }}
        >
            {label}
        </Typography>
        {children}
    </Box>
);

export const SCUIProdcutDetailAllProductTable: FC<{ datas: any }> = ({ datas }) => {
    const { t } = useTranslation();
    const option = datas?.data?.option;

    if (!option) return null;

    const imageUrl = option.medias?.[0]?.mediaPath;
    const status = option.status;

    return (
        <Paper
            className={DSP.paper}
            elevation={0}
            sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                overflow: 'hidden',
                transition: 'box-shadow 0.25s ease',
                '&:hover': { boxShadow: '0 6px 28px rgba(0,0,0,0.09)' },
            }}
        >
            <Box className={DSP.bodyRow} sx={{ display: 'flex', alignItems: 'center', p: 2.5, gap: 3 }}>
                {imageUrl && (
                    <Tooltip
                        title={
                            <Box sx={{ bgcolor: 'white', p: 0.75, borderRadius: 1.5 }}>
                                <img
                                    src={imageUrl}
                                    alt="product"
                                    style={{
                                        maxWidth: 320,
                                        maxHeight: 420,
                                        objectFit: 'contain',
                                        display: 'block',
                                    }}
                                />
                            </Box>
                        }
                        placement="right"
                        arrow
                        slotProps={{
                            tooltip: {
                                sx: {
                                    bgcolor: 'white',
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                                    borderRadius: 2.5,
                                    p: 0.5,
                                    maxWidth: 'none',
                                },
                            },
                            arrow: { sx: { color: 'white' } },
                        }}
                    >
                        <Box
                            className={DSP.thumbnailWrap}
                            component="img"
                            src={imageUrl}
                            alt="product thumbnail"
                            sx={{
                                width: 110,
                                height: 110,
                                objectFit: 'cover',
                                borderRadius: 2,
                                flexShrink: 0,
                                cursor: 'zoom-in',
                                border: '1px solid',
                                borderColor: 'divider',
                                transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                                '&:hover': {
                                    transform: 'scale(1.05)',
                                    boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                                },
                            }}
                        />
                    </Tooltip>
                )}

                <Box
                    className={DSP.fieldsGrid}
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(155px, 1fr))',
                        gap: 2.5,
                        flex: 1,
                        alignContent: 'center',
                    }}
                >
                    <InfoItem label={t('searchService.specialColorCode')}>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 600,
                                color: 'text.primary',
                                fontFamily: 'monospace',
                                fontSize: '0.82rem',
                            }}
                        >
                            {option.ozelKod1} - {option.renkKod}
                        </Typography>
                    </InfoItem>

                    <InfoItem label={t('general.productName')}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary', lineHeight: 1.4 }}>
                            {option.modelTitle?.['tr-TR'] || '—'}
                        </Typography>
                    </InfoItem>

                    <InfoItem label={t('searchService.seasonCode')}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            {option.seasonCode || '—'}
                        </Typography>
                    </InfoItem>

                    <InfoItem label={t('searchService.gender')}>
                        <GenderChip label={option.gender?.cultureValueList?.['tr-TR'] || ''} />
                    </InfoItem>

                    <InfoItem label={t('general.category')}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            {option.categories?.[0]?.cultureValueList?.['tr-TR'] || '—'}
                        </Typography>
                    </InfoItem>

                    <InfoItem label={t('general.status')}>
                        <Chip
                            size="small"
                            icon={
                                status ? (
                                    <CheckCircleRoundedIcon sx={{ fontSize: '14px !important' }} />
                                ) : (
                                    <CancelRoundedIcon sx={{ fontSize: '14px !important' }} />
                                )
                            }
                            label={status + ''}
                            color={datas.data.status ? 'success' : 'error'}
                            sx={{ fontWeight: 700, fontSize: '0.72rem', height: 24 }}
                        />
                    </InfoItem>
                </Box>
            </Box>
        </Paper>
    );
};
